import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { IconThermometer, IconCloud, IconDroplet, IconWind, IconCheck, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import api from '../../../authentication/axiosInstance';
import styles from './StormStrategiesPage.module.css';
import Header from '../../../components/client-care/strategy-history/header/header';
import StatsContainer from '../../../components/client-care/strategy-history/stats-container/stats-container';
import FiltersContainer from '../../../components/client-care/strategy-history/filters-container/filters-container';
import SHTable from '../../../components/client-care/strategy-history/sh-table/sh-table';
import StrategyModal from '../../../components/client-care/strategy-history/strategy-modal/strategy-modal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const EST_TIMEZONE = 'America/New_York';

// Environment variables
const TEST_MODE_EMAIL = ''; // Fill this with the email you'd like to send test emails to

const StormStrategiesPage = () => {
  const [isTestMode, setIsTestMode] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchRegion, setSearchRegion] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [regions, setRegions] = useState([]);
  const [stats, setStats] = useState({
    total_strategies: 0,
    successful_strategies: 0,
    failed_strategies: 0,
    recent_strategies: 0,
    unique_regions: 0
  });
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleKeyDown = useCallback((event) => {
    if (event.ctrlKey && event.altKey && event.key === 't') {
      if (!TEST_MODE_EMAIL) {
        message.error('Test mode is not configured. Please set TEST_MODE_EMAIL first.');
        return;
      }
      setIsTestMode(prev => {
        const newMode = !prev;
        message.success(`Test mode ${newMode ? 'enabled' : 'disabled'}`);
        return newMode;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    fetchStrategies();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/weatherworks/storm-strategies/stats/');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewStrategy = async (strategy) => {
    try {
      // Fetch associated contacts for each site
      const sitesWithContacts = await Promise.all(strategy.sites.map(async (site) => {
        try {
          const response = await api.get(`/sites/${site.site_id}/associated_contacts/`);
          return {
            ...site,
            contacts: response.data
          };
        } catch (error) {
          console.error(`Error fetching contacts for site ${site.site_id}:`, error);
          return {
            ...site,
            contacts: []
          };
        }
      }));

      setSelectedStrategy({
        ...strategy,
        sites: sitesWithContacts
      });
      setModalVisible(true);
    } catch (error) {
      console.error('Error preparing strategy view:', error);
      message.error('Failed to load strategy details');
    }
  };

  const handleSendSurvey = async (strategyId) => {
    try {
      const response = await api.post(`/weatherworks/storm-strategies/${strategyId}/send-survey/`, {
        test_mode: isTestMode
      });
      if (response.data.success) {
        message.success(`Survey email sent successfully${isTestMode ? ' (Test Mode)' : ''}`);
      } else {
        message.error('Failed to send survey email');
      }
    } catch (error) {
      console.error('Error sending survey email:', error);
      message.error('Failed to send survey email');
    }
  };

  const formatToEST = (dateString) => {
    if (!dateString) return null;
    return dayjs(dateString).tz(EST_TIMEZONE).format('MMM D, YYYY h:mm A z');
  };

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/weatherworks/storm-strategies/');
      if (response.data.success) {
        const formattedStrategies = response.data.strategies.map(strategy => ({
          ...strategy,
          formatted_created_at: formatToEST(strategy.created_at),
          formatted_email_sent_at: formatToEST(strategy.email_sent_at)
        }));
        setStrategies(formattedStrategies);
        const uniqueRegions = [...new Set(formattedStrategies.map(s => s.region))];
        setRegions(uniqueRegions);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
      message.error('Failed to fetch storm strategies');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      const [startDate, endDate] = dates;
      const estStartDate = startDate.tz(EST_TIMEZONE).startOf('day');
      const estEndDate = endDate.tz(EST_TIMEZONE).endOf('day');
      setDateRange([estStartDate, estEndDate]);
    } else {
      setDateRange(null);
    }
  };

  const applyFilters = () => {
    let url = '/weatherworks/storm-strategies/?';
    const params = new URLSearchParams();

    if (searchRegion) params.append('region', searchRegion);
    if (searchStatus) params.append('status', searchStatus);
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.append('from_date', dateRange[0].format('YYYY-MM-DD'));
      params.append('to_date', dateRange[1].format('YYYY-MM-DD'));
    }

    fetchStrategiesWithFilters(url + params.toString());
  };

  const fetchStrategiesWithFilters = async (url) => {
    setLoading(true);
    try {
      const response = await api.get(url);
      if (response.data.success) {
        const formattedStrategies = response.data.strategies.map(strategy => ({
          ...strategy,
          formatted_created_at: formatToEST(strategy.created_at),
          formatted_email_sent_at: formatToEST(strategy.email_sent_at)
        }));
        setStrategies(formattedStrategies);
      }
    } catch (error) {
      console.error('Error fetching filtered strategies:', error);
      message.error('Failed to fetch filtered strategies');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      sorter: (a, b) => a.region.localeCompare(b.region),
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 400,
      render: (text) => (
        <div className={styles.messageCell}>
          {text}
        </div>
      ),
    },
    {
      title: 'Sites',
      dataIndex: 'site_count',
      key: 'site_count',
      width: 100,
      sorter: (a, b) => a.site_count - b.site_count,
      render: (count) => (
        <span style={{ fontWeight: 500 }}>{count} sites</span>
      ),
    },
    {
      title: 'Weather Summary',
      key: 'weather_summary',
      width: 200,
      render: (_, record) => {
        const summary = record.weather_summary;
        if (!summary) return 'No weather data';
        return (
          <div className={styles.weatherSummary}>
            <div className={styles.weatherDetail}>
              <IconThermometer size={16} className={styles.weatherIcon} />
              <span>{summary.temperature}°C</span>
            </div>
            <div className={styles.weatherDetail}>
              <IconCloud size={16} className={styles.weatherIcon} />
              <span>{summary.conditions}</span>
            </div>
            <div className={styles.weatherDetail}>
              <IconDroplet size={16} className={styles.weatherIcon} />
              <span>{summary.precipitation}% precipitation</span>
            </div>
            <div className={styles.weatherDetail}>
              <IconWind size={16} className={styles.weatherIcon} />
              <span>{summary.wind_speed} km/h</span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <div className={`${styles.statusBadge} ${styles[status]}`}>
          {status === 'sent' ? (
            <IconCheck size={16} />
          ) : (
            <IconX size={16} />
          )}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </div>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'formatted_created_at',
      key: 'created_at',
      width: 200,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Email Sent At',
      dataIndex: 'formatted_email_sent_at',
      key: 'email_sent_at',
      width: 200,
    }
  ];

  const renderStrategyModal = () => {
    if (!selectedStrategy) return null;

    // Get all contacts from each site
    const recipientsBysite = isTestMode && TEST_MODE_EMAIL ? 
      selectedStrategy.sites?.map(site => ({
        site_name: site.site_name,
        contacts: [{ email: TEST_MODE_EMAIL, first_name: 'Test', last_name: 'Mode' }]
      })) :
      selectedStrategy.sites?.map(site => ({
        site_name: site.site_name,
        contacts: site.contacts || []
      }));

    return (
      <StrategyModal 
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        isTestMode={isTestMode}
        handleSendSurvey={handleSendSurvey}
        selectedStrategy={selectedStrategy}
        recipientsBysite={recipientsBysite}
      />
    );
  };

  return (
    <div className={styles.container}>
      <Header isTestMode={isTestMode} />
      <StatsContainer stats={stats} />
      <FiltersContainer 
        searchRegion={searchRegion}
        setSearchRegion={setSearchRegion}
        regions={regions}
        searchStatus={searchStatus}
        setSearchStatus={setSearchStatus}
        handleDateRangeChange={handleDateRangeChange}
        applyFilters={applyFilters}
      />
      <SHTable 
        strategies={strategies}
        columns={columns}
        loading={loading}
        handleViewStrategy={handleViewStrategy}
      />
      {renderStrategyModal()}
    </div>
  );
};

export default StormStrategiesPage; 